import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Calendar, Cloud, MessageCircle, Send, LogOut, Plus, AlertTriangle, X } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { useStreamingChat } from '../hooks/useStreamingChat';
import StreamingMessage from './StreamingMessage';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { StreamErrorBoundary } from './boundaries/StreamErrorBoundary';
import { parseStructuredResponse, StructuredResponse } from '../utils/chatUtils';
import { NetworkErrorBoundary } from './boundaries/NetworkErrorBoundary';
import { statePreserver } from '../services/statePreserver';

// Message interface
interface Message {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  error?: string;
}

// Structured response interface for itinerary updates

// Performance monitoring interface for baseline metrics
interface PerformanceMetrics {
  messageRenderCount: number;
  averageMessageRenderTime: number;
  averageStreamingLatency: number;
  inputLagMs: number;
  autoScrollTime: number;
  memoryUsageMB: number;
  lastMeasurement: Date;
}

const TravelConcierge: React.FC = () => {
  const { user, currentTrip, logout, createNewTrip, updateTrip, setTrip, storageError, clearStorageError } = useUser();
  
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome-message',
      role: "assistant",
      content: "Welcome! I'm your personal travel concierge. I'm here to help you plan and manage your perfect trip. Tell me where you'd like to go, when you're traveling, and what you're interested in, and I'll create a customized itinerary for you. How may I help you today?",
      timestamp: new Date()
    }
  ]);

  const [inputMessage, setInputMessage] = useState<string>("");
  const [weatherInfo, setWeatherInfo] = useState<string>("Ready to check weather for your destination!");
  
  // PHASE 0: Performance baseline monitoring
  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetrics>({
    messageRenderCount: 0,
    averageMessageRenderTime: 0,
    averageStreamingLatency: 0,
    inputLagMs: 0,
    autoScrollTime: 0,
    memoryUsageMB: 0,
    lastMeasurement: new Date()
  });
  
  // Streaming chat hook
  const { 
    sendStreamingMessage, 
    isStreaming, 
    currentStreamingText, 
    streamingError, 
  } = useStreamingChat();
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const itineraryRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Performance measurement refs
  const renderStartTime = useRef<number>(0);
  const inputStartTime = useRef<number>(0);
  const streamingStartTime = useRef<number>(0);
  const scrollStartTime = useRef<number>(0);
  
  // Performance measurement functions
  const measurePerformance = useCallback(() => {
    const now = Date.now();
    const memoryInfo = (performance as any).memory;
    const memoryUsageMB = memoryInfo ? memoryInfo.usedJSHeapSize / (1024 * 1024) : 0;
    
    setPerformanceMetrics(prev => ({
      ...prev,
      memoryUsageMB: Math.round(memoryUsageMB * 100) / 100,
      lastMeasurement: new Date(now)
    }));
    
    // Log metrics to console for Phase 0 baseline collection
    console.log('📊 Performance Baseline:', {
      messageCount: messages.length,
      averageRenderTime: performanceMetrics.averageMessageRenderTime,
      streamingLatency: performanceMetrics.averageStreamingLatency,
      inputLag: performanceMetrics.inputLagMs,
      memoryMB: Math.round(memoryUsageMB * 100) / 100,
      timestamp: new Date().toISOString()
    });
  }, [messages.length, performanceMetrics.averageMessageRenderTime, performanceMetrics.averageStreamingLatency, performanceMetrics.inputLagMs]);

  const measureMessageRender = useCallback(() => {
    const renderTime = performance.now() - renderStartTime.current;
    setPerformanceMetrics(prev => ({
      ...prev,
      messageRenderCount: prev.messageRenderCount + 1,
      averageMessageRenderTime: prev.messageRenderCount === 0 ? renderTime : 
        (prev.averageMessageRenderTime * prev.messageRenderCount + renderTime) / (prev.messageRenderCount + 1)
    }));
  }, []);

  const measureInputLag = useCallback(() => {
    const inputLag = performance.now() - inputStartTime.current;
    setPerformanceMetrics(prev => ({
      ...prev,
      inputLagMs: Math.max(prev.inputLagMs, inputLag) // Track maximum input lag
    }));
  }, []);

  const measureScrollTime = useCallback(() => {
    const scrollTime = performance.now() - scrollStartTime.current;
    setPerformanceMetrics(prev => ({
      ...prev,
      autoScrollTime: scrollTime
    }));
  }, []);

  const measureStreamingLatency = useCallback((latency: number) => {
    setPerformanceMetrics(prev => ({
      ...prev,
      averageStreamingLatency: prev.averageStreamingLatency === 0 ? latency :
        (prev.averageStreamingLatency + latency) / 2
    }));
  }, []);

  // Auto-scroll to bottom of messages with performance measurement
  useEffect(() => {
    scrollStartTime.current = performance.now();
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    // Measure scroll time after a brief delay to account for smooth scrolling
    setTimeout(measureScrollTime, 150);
  }, [messages, measureScrollTime]);

  // Sync panel heights after content changes
  useEffect(() => {
    const syncHeights = () => {
      if (itineraryRef.current && chatRef.current) {
        const itineraryHeight = itineraryRef.current.scrollHeight;
        const chatHeight = chatRef.current.scrollHeight;
        const maxHeight = Math.max(itineraryHeight, chatHeight);
        
        // Set both panels to the same height
        itineraryRef.current.style.minHeight = `${maxHeight}px`;
        chatRef.current.style.minHeight = `${maxHeight}px`;
      }
    };

    // Sync heights after a short delay to ensure content is rendered
    const timer = setTimeout(syncHeights, 100);
    return () => clearTimeout(timer);
  }, [currentTrip?.activities, messages]);

  // Load trip messages when switching trips or on initial load
  useEffect(() => {
    if (currentTrip?._id) {
      const welcomeMessage = {
        id: 'welcome-message',
        role: "assistant" as const,
        content: currentTrip.messages?.length > 0 
          ? "I'm back! I have your trip details and our conversation history. How can I help you today?"
          : "Welcome! I'm your personal travel concierge. I'm here to help you plan and manage your perfect trip. Tell me where you'd like to go, when you're traveling, and what you're interested in, and I'll create a customized itinerary for you. How may I help you today?",
        timestamp: new Date()
      };
      
      if (currentTrip.messages && currentTrip.messages.length > 0) {
        // Load existing conversation with welcome message
        setMessages([
          welcomeMessage,
          ...currentTrip.messages.map((msg, index) => ({
            ...msg,
            id: `loaded-msg-${index}`,
            timestamp: new Date(msg.timestamp)
          }))
        ]);
      } else {
        // New trip - just show welcome message
        setMessages([welcomeMessage]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrip?._id]); // Only reset when trip ID changes

  // Simulate weather updates every 30 seconds (only if we have a destination)
  useEffect(() => {
    if (!currentTrip?.destination) return;
    
    const weatherUpdates = [
      "Partly cloudy, 12°C - Perfect for walking!",
      "Light rain expected at 4 PM - Consider indoor activities",
      "Sunny and clear, 15°C - Excellent day for outdoor sightseeing!",
      "Cloudy with occasional sun, 10°C - Pack a light jacket"
    ];
    
    const interval = setInterval(() => {
      const randomWeather = weatherUpdates[Math.floor(Math.random() * weatherUpdates.length)];
      setWeatherInfo(randomWeather);
    }, 30000);

    return () => clearInterval(interval);
  }, [currentTrip?.destination]);

  // Parse JSON from streamed response for itinerary updates

  const sendMessageToAI = async (userMessage: string): Promise<void> => {
    if (!currentTrip) {
      // Create new trip if none exists
      try {
        await createNewTrip();
      } catch (error) {
        console.error('Failed to create trip:', error);
        return;
      }
    }

    try {
      // Add user message to UI immediately
      const userMsgId = `user-${Date.now()}`;
      const userMsg: Message = {
        id: userMsgId,
        role: "user",
        content: userMessage,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, userMsg]);
      
      // Build messages array for this API call (avoid race condition)
      const currentMessages = [...(currentTrip?.messages || []), userMsg];
      
      // Save user message to UserContext immediately
      updateTrip({
        messages: currentMessages
      });

      // Create streaming assistant message placeholder
      const assistantMsgId = `assistant-${Date.now()}`;
      const streamingMsg: Message = {
        id: assistantMsgId,
        role: "assistant",
        content: "",
        timestamp: new Date(),
        isStreaming: true
      };
      setMessages(prev => [...prev, streamingMsg]);

      // Convert messages to format expected by streaming hook
      const conversationHistory = messages.slice(1).map(msg => ({
        id: msg.id || `msg-${Date.now()}-${Math.random()}`,
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp
      }));

      // Checkpoint state before streaming (for error recovery)
      statePreserver.checkpoint('chat_state', {
        userMessage,
        conversationHistory,
        currentTripId: currentTrip?._id || null,
        timestamp: new Date().toISOString()
      });

      // PHASE 0: Measure streaming latency
      streamingStartTime.current = performance.now();
      
      // Use streaming API only (no fallback)
      const streamedResponse = await sendStreamingMessage(userMessage, conversationHistory);
      
      // Measure streaming latency
      const streamingLatency = performance.now() - streamingStartTime.current;
      measureStreamingLatency(streamingLatency);

      // Check for structured response (itinerary updates)
      const { response: structuredResponse, cleanedContent } = parseStructuredResponse(streamedResponse);
      
      let finalContent = cleanedContent; // Use cleaned content without JSON
      if (structuredResponse && structuredResponse.action === 'update_itinerary') {
        // Update itinerary and show success message
        finalContent = structuredResponse.message + "\n\n✅ Itinerary updated successfully!";
        
        // Create new trip if none exists, otherwise update existing
        if (!currentTrip) {
          const newTrip = await createNewTrip();
          
          // Use setTrip to directly set the trip with itinerary data
          // This bypasses the updateTrip early return issue with React state timing
          const finalAssistantMsg: Message = {
            id: assistantMsgId,
            role: "assistant",
            content: finalContent,
            timestamp: new Date(),
            isStreaming: false,
            error: streamingError || undefined
          };
          const allMessages = [...currentMessages, finalAssistantMsg];
          
          const tripWithItinerary = {
            ...newTrip,
            ...structuredResponse.itinerary,
            conversationSummary: structuredResponse.conversationSummary || '',
            messages: allMessages  // Preserve the complete conversation history
          };
          
          setTrip(tripWithItinerary);
        } else {
          const updateData = {
            ...structuredResponse.itinerary,
            conversationSummary: structuredResponse.conversationSummary || currentTrip.conversationSummary || ''
          };
          updateTrip(updateData);
        }
      }

      // Update message with final content
      const finalAssistantMsg: Message = {
        id: assistantMsgId,
        role: "assistant",
        content: finalContent,
        timestamp: new Date(),
        isStreaming: false,
        error: streamingError || undefined
      };

      // Update UI with final message
      setMessages(prev => prev.map(msg => 
        msg.id === assistantMsgId ? finalAssistantMsg : msg
      ));

      // Save final assistant message to UserContext only if we didn't already handle it in structured response
      if (!(structuredResponse && structuredResponse.action === 'update_itinerary')) {
        const allMessages = [...currentMessages, finalAssistantMsg];
        updateTrip({
          messages: allMessages
        });
      }

    } catch (error) {
      console.error("Error calling AI:", error);
      const errorMsg = {
        id: `error-${Date.now()}`,
        role: "assistant" as const,
        content: "Sorry, I encountered an error. Please try again.",
        timestamp: new Date(),
        error: error instanceof Error ? error.message : 'Unknown error'
      };
      
      // Save error message to UserContext
      updateTrip({
        messages: [...(currentTrip?.messages || []), errorMsg]
      });
      
      // Add to UI
      setMessages(prev => [...prev, errorMsg]);
    }
  };

  const handleSendMessage = (): void => {
    if (inputMessage.trim() && !isStreaming) {
      sendMessageToAI(inputMessage);
      setInputMessage("");
    }
  };

  // Auto-resize textarea based on content
  const resizeTextarea = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '48px'; // Reset to min height
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 120; // max-h-[120px]
      textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  }, []);

  // Resize textarea when input changes
  useEffect(() => {
    resizeTextarea();
  }, [inputMessage, resizeTextarea]);

  // PHASE 0: Performance monitoring interval for baseline data collection
  useEffect(() => {
    console.log('🚀 PHASE 0: Performance monitoring enabled - collecting baseline metrics');
    
    // Measure performance every 30 seconds
    const performanceInterval = setInterval(measurePerformance, 30000);
    
    // Initial measurement
    measurePerformance();
    
    return () => {
      clearInterval(performanceInterval);
      console.log('📊 PHASE 0: Final performance metrics:', performanceMetrics);
    };
  }, [measurePerformance, performanceMetrics]);

  const handleNewTrip = async (): Promise<void> => {
    try {
      await createNewTrip();
      // Messages will be reset automatically by the useEffect when currentTrip._id changes
      // No need to manually set messages here since useEffect will handle it
    } catch (error) {
      console.error('Failed to create new trip:', error);
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <NetworkErrorBoundary
      onRetry={() => {
        // Clear any error states and attempt to recover
        setMessages([{
          id: 'welcome-message',
          role: "assistant",
          content: "I'm back online! How can I help you with your travel plans?",
          timestamp: new Date()
        }]);
      }}
    >
      <div className="min-h-screen bg-background p-4">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-4">
        <div className="flex justify-between items-center bg-card rounded-xl shadow-lg p-4 border border-border">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-foreground">Welcome, {user.name}</h1>
          </div>
          <div className="flex gap-2">
            <Link to="/company">
              <Button variant="ghost" className="text-muted-foreground hover:text-foreground">
                About
              </Button>
            </Link>
            <Button 
              onClick={handleNewTrip}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              New Trip
            </Button>
            <Button 
              onClick={logout}
              variant="secondary"
              className="flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Storage Error Notification */}
      {storageError && (
        <div className="max-w-6xl mx-auto mb-4">
          <div className="bg-accent/10 border-l-4 border-accent p-4 rounded-lg shadow-md">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-accent" />
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm text-accent-foreground">
                  <strong>Storage Warning:</strong> {storageError}
                </p>
              </div>
              <div className="ml-auto pl-3">
                <Button
                  onClick={clearStorageError}
                  variant="ghost"
                  size="sm"
                  className="text-accent hover:opacity-70"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-6">
        
        {/* Left Panel - Itinerary */}
        <div ref={itineraryRef} className="bg-card rounded-2xl shadow-xl p-6 flex-1 border border-border">
          <div className="flex items-center gap-3 mb-6">
            <MapPin className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-bold text-card-foreground">Your Itinerary</h2>
          </div>

          <div className="mb-6">
            {currentTrip?.destination ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-5 h-5 text-muted-foreground" />
                  <span className="font-semibold text-foreground">{currentTrip.destination}</span>
                </div>
                <p className="text-muted-foreground">{currentTrip.dates}</p>
                <p className="text-sm text-primary mt-1">Status: {currentTrip.status}</p>
              </>
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-muted mx-auto mb-4" />
                <p className="text-muted-foreground text-lg">No trip planned yet</p>
                <p className="text-muted-foreground/70 text-sm">Start chatting with your concierge to plan your perfect trip!</p>
              </div>
            )}
          </div>

          {/* Weather Info */}
          {currentTrip?.destination && (
            <div className="bg-primary/10 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Cloud className="w-5 h-5 text-primary" />
                <span className="font-semibold text-primary">Current Weather</span>
              </div>
              <p className="text-primary/90">{weatherInfo}</p>
            </div>
          )}

          {/* Activities */}
          {currentTrip?.activities && currentTrip.activities.length > 0 ? (
            <div className="space-y-6">
              {currentTrip.activities.map((dayPlan) => (
                <div key={dayPlan.id} className="bg-muted rounded-lg p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-bold text-primary text-lg">{dayPlan.day}</h3>
                  </div>
                  <div className="space-y-3">
                    {dayPlan.activities?.map((activity, index) => (
                      <div key={index} className="flex gap-4 bg-background rounded p-3 hover:bg-accent/5 transition-colors border border-border/50">
                        <Badge variant="secondary" className="text-sm font-medium min-w-[80px] justify-center">
                          {activity.time}
                        </Badge>
                        <div className="flex-1">
                          <p className="text-foreground">{activity.activity}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            currentTrip?.destination && (
              <div className="text-center py-8">
                <MapPin className="w-12 h-12 text-muted mx-auto mb-4" />
                <p className="text-muted-foreground">No activities planned yet</p>
                <p className="text-muted-foreground/70 text-sm">Ask your concierge to add some activities!</p>
              </div>
            )
          )}
        </div>

        {/* Right Panel - Chat */}
        <StreamErrorBoundary
          fallbackMessage="Chat temporarily unavailable due to streaming issues. Messages will still be processed, but without real-time updates."
        >
          <div ref={chatRef} className="bg-card rounded-2xl shadow-xl p-6 flex flex-col flex-1 border border-border">
            <div className="flex items-center gap-3 mb-6 flex-shrink-0">
              <MessageCircle className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-card-foreground">Chat with Your Concierge</h2>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto space-y-4 mb-4 min-h-0">
            {messages.map((message, index) => {
              // PHASE 0: Measure message render time
              renderStartTime.current = performance.now();
              
              const messageElement = (
                <div
                  key={message.id || index}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  ref={() => {
                    // Measure render time after element is in DOM
                    requestAnimationFrame(measureMessageRender);
                  }}
                >
                  {message.role === 'user' ? (
                    <div className="max-w-[80%] p-3 rounded-lg bg-primary text-primary-foreground rounded-br-none">
                      <div className="text-sm whitespace-pre-line">
                        {message.content}
                      </div>
                      <p className="text-xs mt-1 text-primary-foreground/80">
                        {new Date(message.timestamp).toLocaleTimeString()}
                      </p>
                    </div>
                  ) : (
                    <StreamingMessage 
                      content={
                        message.isStreaming 
                          ? currentStreamingText 
                          : message.content
                      }
                      isStreaming={message.isStreaming || false}
                      error={message.error || streamingError || undefined}
                    />
                  )}
                </div>
              );
              
              return messageElement;
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="flex gap-2 flex-shrink-0">
            <Textarea
              ref={textareaRef}
              value={inputMessage}
              onChange={(e) => {
                inputStartTime.current = performance.now();
                setInputMessage(e.target.value);
                // Measure input lag on next frame
                requestAnimationFrame(measureInputLag);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              placeholder={currentTrip?.destination ? "Ask about restaurants, weather, or modify your itinerary..." : "Where would you like to go? Tell me about your dream trip..."}
              className="flex-1 resize-none overflow-auto min-h-[48px] max-h-[120px]"
              disabled={isStreaming}
              rows={1}
            />
            <Button
              onClick={handleSendMessage}
              disabled={isStreaming || !inputMessage.trim()}
              size="lg"
              className="p-3"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>

          {/* Quick Actions */}
          <div className="mt-4 flex flex-wrap gap-2 flex-shrink-0">
            {(currentTrip?.destination ? [
              "What's the weather like?",
              "Recommend a restaurant near my first activity",
              "What should I pack?",
              "Add a day trip somewhere nearby",
              "Change day 2 to something different"
            ] : [
              "Plan a trip to Paris for 5 days",
              "I want to visit Japan in spring",
              "Help me plan a California road trip",
              "Plan a weekend in New York"
            ]).map((suggestion, index) => (
              <Button
                key={index}
                onClick={() => {
                  setInputMessage(suggestion);
                  sendMessageToAI(suggestion);
                }}
                variant="secondary"
                size="sm"
                className="text-xs rounded-full"
                disabled={isStreaming}
              >
                {suggestion}
              </Button>
            ))}
          </div>
        </div>
        </StreamErrorBoundary>
      </div>
      </div>
    </NetworkErrorBoundary>
  );
};

export default TravelConcierge;